<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core'
import { getSrcSet, getParams, PictureSizePreset } from './mediaHelpers'

defineOptions({
  inheritAttrs: false,
})

const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

type CommonPictureFields = {
  fields: {
    file: {
      url: string
      contentType: 'image/jpeg'
      details?: { image?: { width: number; height: number } }
    }
  }
}

export type PictureFields = {
  picture: CommonPictureFields
  mobilePicture: CommonPictureFields
  pictureLinkUrl?: string
  /*
   * TODO: remove backwards-compatibility tech debt — legacy reference field, replaced by
   * pictureLinkUrl to remove the cycle topology (picture.pictureLink → link.quickLinkPicture
   * → picture). Read-only fallback until the data sync + field removal migration is complete;
   * once a full release cycle has passed this field and its fallback can be deleted.
   */
  pictureLink?: {
    fields: {
      url: string
    }
  }
  mobileUrl?: string // video - desktop
  url?: string // video - mobile
  altText?: string
  quality?: string
}

export type PicturePreset = PictureSizePreset | undefined

export type PictureProps = {
  fields?: PictureFields
  preset?: PicturePreset | undefined
  loading?: 'lazy' | 'eager' | undefined
  fetchpriority?: 'high' | 'low' | 'auto' | undefined
}

const props = withDefaults(defineProps<PictureProps>(), {
  preset: 'full',
  loading: 'lazy',
})
const attrs = useAttrs()

const isPriority = inject(MEDIA_PRIORITY_KEY, false)
const loading = isPriority ? 'eager' : props.loading
const fetchpriority = isPriority ? props.fetchpriority || 'high' : props.fetchpriority

const fields = props.fields
const alt = fields?.altText || ''
// TODO - once pictureLinkUrl is fully rolled out and pictureLink field is removed, simplify to just fields?.pictureLinkUrl
const pictureLinkUrl = fields?.pictureLinkUrl || fields?.pictureLink?.fields?.url
let quality = fields?.quality?.replace('Default', '90') || '90'
quality = quality === '100' ? '99' : quality // 100 means no compression!

const srcDesktop = fields?.picture?.fields?.file?.url
const srcsetDesktopXL = getSrcSet({ src: srcDesktop, preset: props.preset, breakpoint: 'desktopXL', quality })
const srcsetDesktop = getSrcSet({ src: srcDesktop, preset: props.preset, breakpoint: 'desktop', quality })
const srcsetTablet = getSrcSet({ src: srcDesktop, preset: props.preset, breakpoint: 'tablet', quality })

const desktopDimensions = fields?.picture?.fields?.file?.details?.image
const mobileDimensions = fields?.mobilePicture?.fields?.file?.details?.image || desktopDimensions

const srcMobile = fields?.mobilePicture?.fields?.file?.url || srcDesktop
const srcsetMobile = getSrcSet({ src: srcMobile, preset: props.preset, breakpoint: 'mobile', quality })

// The first panel's image sits behind the header's mega-menu markup, so announce it from <head>.
if (isPriority && srcDesktop) {
  const sources = [
    { media: '(min-width: 2561px)', imagesrcset: srcsetDesktopXL },
    { media: '(min-width: 1280px) and (max-width: 2560px)', imagesrcset: srcsetDesktop },
    { media: '(min-width: 768px) and (max-width: 1279px)', imagesrcset: srcsetTablet },
    { media: '(max-width: 767px)', imagesrcset: srcsetMobile },
  ]
  useHead({
    link: sources.map((source) => ({
      key: `lcp-image:${source.imagesrcset}`,
      rel: 'preload',
      as: 'image',
      type: 'image/webp',
      fetchpriority: 'high',
      ...source,
    })),
  })
}

let srcFallback = fields?.picture?.fields?.file?.url || ''
srcFallback = srcFallback
  ? `${srcFallback}?${getParams({ preset: props.preset, breakpoint: 'desktop', quality, format: 'jpg' })}`
  : ''
</script>

<template>
  <DefineTemplate>
    <picture class="picture" :class="attrs.class">
      <source type="image/webp" media="(min-width: 2561px)" :srcset="srcsetDesktopXL" />
      <source type="image/webp" media="(min-width: 1280px)" :srcset="srcsetDesktop" />
      <source type="image/webp" media="(min-width: 768px)" :srcset="srcsetTablet" />
      <source
        type="image/webp"
        media="(max-width: 767px)"
        :srcset="srcsetMobile"
        :width="mobileDimensions?.width"
        :height="mobileDimensions?.height"
      />
      <!-- @vue-ignore -->
      <img
        class="picture__img"
        data-testid="picture__img"
        :src="srcFallback"
        :alt="alt"
        :loading="loading"
        :fetchpriority="fetchpriority"
        :width="desktopDimensions?.width"
        :height="desktopDimensions?.height"
      />
    </picture>
  </DefineTemplate>

  <div class="picture-container">
    <Anchor v-if="pictureLinkUrl" :url="pictureLinkUrl" class="picture-link">
      <ReuseTemplate />
    </Anchor>
    <template v-else-if="fields">
      <ReuseTemplate />
    </template>
  </div>
</template>

<style>
/* width/height attributes reserve the aspect ratio before the image loads; height must still follow width. */
.picture__img {
  height: auto;
}
</style>
